// src/pages/destinations/useFilteredAudiences.ts

import { SelectChangeEvent } from "@mui/material";
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { handleMultiSelectWithAll } from "./filterAllHelper";

type ParentTree = {
  folder1: string;
  children?: DestinationNode[];
};

type DestinationNode = {
  name: string;
  status: string;
  brand: string;
  type: "folder" | "destination";
  market: string;
  country: string;
  createdby: string;
  creation_time: string;
  last_modified_by: string;
  last_modified_time: string;
  count: number;
  campaigns: string[];
  children?: DestinationNode[];
};

export const useFilteredAudiences = ({ parseDataFolderDropdown }: any) => {
  const rawAudienceTree = useMemo(() => {
    if (!Array.isArray(parseDataFolderDropdown)) return [];
    return parseDataFolderDropdown as ParentTree[];
  }, [parseDataFolderDropdown]);

  const flatAudienceList = useMemo(() => {
    function flattenDestinations(
      nodes: DestinationNode[] | undefined,
      parentCountry: string
    ): DestinationNode[] {
      if (!nodes) return [];
      return nodes.flatMap((node) => {
        const nodeWithCountry = { ...node, country: parentCountry };

        return node.type === "destination"
          ? [nodeWithCountry]
          : flattenDestinations(node.children, parentCountry);
      });
    }

    return rawAudienceTree.flatMap((folder1) => flattenDestinations(folder1.children, folder1.folder1));
  }, [rawAudienceTree]);

  const getUniqueOptions = (key: keyof DestinationNode): string[] => {
    const unique = new Set<string>();
    flatAudienceList.forEach((item) => {
      if (item[key]) {
        unique.add(item[key] as string);
      }
    });
    return ["All", ...Array.from(unique).sort()];
  };

  const statusOptions = getUniqueOptions("status");
  const marketOptions = getUniqueOptions("market");
  const brandOptions = getUniqueOptions("brand");
  const createdByOptions = getUniqueOptions("createdby");
  const lastEditedByOptions = getUniqueOptions("last_modified_by");

  const [inputNameValue, setInputNameValue] = useState("");
  const [brand, setBrand] = useState<string[]>([...brandOptions]);
  const [status, setStatus] = useState<string[]>([...statusOptions]);
  const [market, setDataset] = useState<string[]>([...marketOptions]);
  const [createdBy, setCreatedBy] = useState<string[]>([...createdByOptions]);
  const [lastEditedBy, setLastEditedBy] = useState<string[]>([...lastEditedByOptions]);

  const filteredData = useMemo(() => {
    if (
      !inputNameValue.trim() &&
      brand.includes("All") &&
      status.includes("All") &&
      market.includes("All") &&
      createdBy.includes("All") &&
      lastEditedBy.includes("All")
    )
      return rawAudienceTree;

    const filterDestinations = (nodes?: DestinationNode[]): DestinationNode[] => {
      if (!nodes) return [];

      return nodes
        .map((node) => {
          const matchesName =
            !inputNameValue.trim() ||
            node.name?.toLowerCase().includes(inputNameValue.trim().toLowerCase());

          const matchesBrand = brand.includes("All") || brand.includes(node.brand);

          const matchesStatus = status.includes("All") || status.includes(node.status);

          const matchesDataset = market.includes("All") || market.includes(node.market);

          const matchesCreatedBy = createdBy.includes("All") || createdBy.includes(node.createdby);

          const matchesLastEditedBy =
            lastEditedBy.includes("All") || lastEditedBy.includes(node.last_modified_by);

          const thisNodeMatches =
            matchesName &&
            matchesBrand &&
            matchesStatus &&
            matchesDataset &&
            matchesCreatedBy &&
            matchesLastEditedBy;

          const filteredChildren = filterDestinations(node.children);

          if (thisNodeMatches || filteredChildren.length > 0) {
            return {
              ...node,
              children: filteredChildren,
            };
          }
          return null;
        })
        .filter(Boolean) as DestinationNode[];
    };

    return rawAudienceTree
      .map((folder1) => ({
        ...folder1,
        children: filterDestinations(folder1.children),
      }))
      .filter((folder1) => folder1.children && folder1.children.length > 0);
  }, [inputNameValue, brand, status, market, createdBy, lastEditedBy, rawAudienceTree]);

  const handleSelectChange = (
    event: SelectChangeEvent<string[]>,
    currentState: string[],
    setState: (val: string[]) => void,
    allOptions: string[]
  ) => {
    const value =
      typeof event.target.value === "string" ? event.target.value.split(",") : event.target.value;
    const checked = value.length > currentState.length;
    const changedOption = checked
      ? value.find((opt) => !currentState.includes(opt))!
      : currentState.find((opt) => !value.includes(opt))!;

    setState(handleMultiSelectWithAll(currentState, changedOption, allOptions, checked));
  };

  const handleInputNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputNameValue(e.target.value);
  };

  const clearFilters = () => {
    setBrand(brandOptions.length > 0 ? [...brandOptions] : ["All"]);
    setStatus(statusOptions.length > 0 ? [...statusOptions] : ["All"]);
    setDataset(marketOptions.length > 0 ? [...marketOptions] : ["All"]);
    setCreatedBy(createdByOptions.length > 0 ? [...createdByOptions] : ["All"]);
    setLastEditedBy(lastEditedByOptions.length > 0 ? [...lastEditedByOptions] : ["All"]);
  };

  const hasClearedFiltersAfterLoad = useRef(false);

  useEffect(() => {
    if (rawAudienceTree.length > 0) {
      if (!hasClearedFiltersAfterLoad.current) {
        setBrand(brandOptions.length > 0 ? [...brandOptions] : ["All"]);
        setStatus(statusOptions.length > 0 ? [...statusOptions] : ["All"]);
        setDataset(marketOptions.length > 0 ? [...marketOptions] : ["All"]);
        setCreatedBy(createdByOptions.length > 0 ? [...createdByOptions] : ["All"]);
        setLastEditedBy(lastEditedByOptions.length > 0 ? [...lastEditedByOptions] : ["All"]);
        hasClearedFiltersAfterLoad.current = true;
      }
    }
  }, [
    rawAudienceTree,
    filteredData,
    brandOptions,
    statusOptions,
    marketOptions,
    createdByOptions,
    lastEditedByOptions,
  ]);

  return {
    inputNameValue,
    setInputNameValue,
    handleInputNameChange,
    handleStatusChange: (e: SelectChangeEvent<string[]>) =>
      handleSelectChange(e, status, setStatus, statusOptions),
    handleDatasetChange: (e: SelectChangeEvent<string[]>) =>
      handleSelectChange(e, market, setDataset, marketOptions),
    handleCreatedByChange: (e: SelectChangeEvent<string[]>) =>
      handleSelectChange(e, createdBy, setCreatedBy, createdByOptions),
    handleLastEditedChange: (e: SelectChangeEvent<string[]>) =>
      handleSelectChange(e, lastEditedBy, setLastEditedBy, lastEditedByOptions),
    handleBrandChange: (e: SelectChangeEvent<string[]>) =>
      handleSelectChange(e, brand, setBrand, brandOptions),
    status,
    market,
    createdBy,
    lastEditedBy,
    brand,
    statusOptions,
    marketOptions,
    createdByOptions,
    lastEditedByOptions,
    brandOptions,
    rawAudienceTree,
    filteredData,
    clearFilters,
  };
};
